/**
 * Achat et pose d'une décoration en gemmes — **logique pure** (ROADMAP 2.4, 2.9a, 2.9c).
 *
 * ══ Le modèle : acheter et poser sont DEUX gestes ════════════════════════════
 *
 * L'achat encaisse et met l'objet au **sac à dos** ; la pose l'en sort, vers la case que
 * le joueur a visée au fantôme. Cette séparation n'est pas un détail d'interface :
 *
 * - **aucun chemin ne perd un objet payé** — annuler un placement le laisse au sac,
 *   le prestige y range tout ce qui était posé, une ville pleine n'empêche plus
 *   d'acheter (le refus « pas de place » n'existe plus : on pose quand on veut) ;
 * - **la place se vérifie à la pose, pas à l'achat** — encaisser puis échouer à poser
 *   laissait le joueur avec un solde en moins ; désormais l'échec de pose ne consomme
 *   rien, l'objet attend au sac.
 *
 * Le §14 n'a rien à surveiller ici : une décoration ne produit **rien**, pas « peu ».
 * La frontière est vraie par construction, aucun équilibrage futur ne peut la déplacer.
 */

import { decorationById } from '@/config/decorations';

import type { BackpackItem, CitySnapshot, VisitorTrace } from './gameState';

export type BuyDecorationRefusal = 'unknown' | 'too-few-gems';

export type BuyDecorationResult =
  | { readonly ok: true; readonly snapshot: CitySnapshot; readonly item: BackpackItem }
  | { readonly ok: false; readonly refusal: BuyDecorationRefusal };

/**
 * Achète une décoration : encaisse et la range au sac à dos.
 *
 * L'appelant enchaîne normalement sur le mode de pose (2.9a) — mais si le joueur
 * annule, ferme l'onglet ou perd sa connexion, l'objet payé est déjà en sécurité.
 */
export function buyDecoration(
  snapshot: CitySnapshot,
  id: string,
  now: number,
): BuyDecorationResult {
  const definition = decorationById(id);
  if (definition === undefined) return { ok: false, refusal: 'unknown' };
  if (snapshot.gems < definition.gemCost) return { ok: false, refusal: 'too-few-gems' };

  const item: BackpackItem = {
    // L'horodatage seul ne suffit pas : deux achats dans la même milliseconde — un
    // double tap, un test — produiraient deux entités du même identifiant. Le compteur
    // d'entrées du sac désambiguïse.
    id: `dec-${String(now)}-${String(snapshot.backpack.length)}-${id}`,
    decorationId: id,
  };

  return {
    ok: true,
    item,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems - definition.gemCost,
      backpack: [...snapshot.backpack, item],
      updatedAt: now,
    },
  };
}

export type PlaceDecorationRefusal = 'not-found' | 'unknown' | 'cell-occupied' | 'no-island';

export type PlaceDecorationResult =
  | { readonly ok: true; readonly snapshot: CitySnapshot; readonly placed: VisitorTrace }
  | { readonly ok: false; readonly refusal: PlaceDecorationRefusal };

/**
 * Pose une décoration du sac à dos sur la case visée.
 *
 * `occupied` est fourni par l'appelant : `core` ne connaît pas l'emprise des bâtiments,
 * qui vit dans le rendu. Même contrat que `spawnVisitorTraces` et `moveVisitorTrace`.
 * Un refus ne consomme RIEN — l'objet reste au sac, le joueur vise ailleurs.
 */
export function placeDecoration(
  snapshot: CitySnapshot,
  itemId: string,
  col: number,
  row: number,
  occupied: ReadonlySet<string>,
  now: number,
): PlaceDecorationResult {
  const item = snapshot.backpack.find((entry) => entry.id === itemId);
  if (item === undefined) return { ok: false, refusal: 'not-found' };

  const definition = decorationById(item.decorationId);
  if (definition === undefined) return { ok: false, refusal: 'unknown' };

  const island = snapshot.islands[0];
  if (island === undefined) return { ok: false, refusal: 'no-island' };

  const { cols, rows } = island.grid;
  if (col < 0 || row < 0 || col >= cols || row >= rows) {
    return { ok: false, refusal: 'cell-occupied' };
  }
  if (occupied.has(`${String(col)}:${String(row)}`)) {
    return { ok: false, refusal: 'cell-occupied' };
  }

  const placed: VisitorTrace = {
    // L'identifiant du sac SUIT l'objet : acheté, rangé, posé — même entité de bout en
    // bout, et le préfixe `dec-` reste le fait persisté qui dit l'origine (0.63.2).
    id: item.id,
    kind: definition.sprite,
    col,
    row,
    placedAt: now,
    bought: true,
  };

  return {
    ok: true,
    placed,
    snapshot: {
      ...snapshot,
      backpack: snapshot.backpack.filter((entry) => entry.id !== itemId),
      islands: snapshot.islands.map((entry, index) =>
        index === 0 ? { ...entry, traces: [...entry.traces, placed] } : entry,
      ),
      updatedAt: now,
    },
  };
}
